import { LitElement, html, css } from 'lit';
import { designSystem, componentStyles } from '../../styles/design-system.js';
import { phoneStyles } from '../../styles/phone-styles.js';

/**
 * Phone Number Config Form — sub-component
 * Renders the add/edit form fields for a phone number.
 * Communicates with parent via CustomEvents.
 */
export class PhoneNumberModal extends LitElement {
  static properties = {
    data: { type: Object },
    agents: { type: Array },
    mode: { type: String },         // 'add' | 'edit'
    isFromTwilio: { type: Boolean }, // phone field locked
    saving: { type: Boolean }
  };

  static styles = [designSystem, componentStyles, phoneStyles, css`
    :host {
      display: flex;
      flex-direction: column;
      gap: var(--space-5);
    }
  `];

  constructor() {
    super();
    this.data = {};
    this.agents = [];
    this.mode = 'add';
    this.isFromTwilio = false;
    this.saving = false;
  }

  _emit(field, value) {
    this.dispatchEvent(new CustomEvent('field-change', {
      detail: { field, value }, bubbles: true, composed: true
    }));
  }

  render() {
    const d = this.data || {};
    const isPhoneLocked = this.isFromTwilio || this.mode === 'edit';

    return html`
      <!-- Basic Info -->
      <div class="form-row">
        <div class="form-group">
          <label class="form-label">Phone Number *</label>
          <input type="text" class="form-input" placeholder="+15551234567"
            .value=${d.twilio_number || ''}
            @input=${(e) => this._emit('twilio_number', e.target.value)}
            ?readonly=${isPhoneLocked}
            style="font-family: var(--font-mono); ${isPhoneLocked ? 'opacity: 0.7; cursor: not-allowed;' : ''}"
          />
          <span class="form-hint">${isPhoneLocked ? 'Imported from Twilio' : 'E.164 format'}</span>
        </div>
        <div class="form-group">
          <label class="form-label">Label</label>
          <input type="text" class="form-input" placeholder="Main Reservations"
            .value=${d.label || ''}
            @input=${(e) => this._emit('label', e.target.value)}
          />
        </div>
      </div>

      <!-- Assignment -->
      <div class="form-row">
        <div class="form-group">
          <label class="form-label">Agent</label>
          <select class="form-select"
            .value=${String(d.agent_id || '')}
            @change=${(e) => this._emit('agent_id', e.target.value ? parseInt(e.target.value) : null)}>
            <option value="">Unassigned</option>
            ${(this.agents || []).map(a => html`
              <option value=${a.id} ?selected=${String(d.agent_id) === String(a.id)}>${a.name}</option>
            `)}
          </select>
        </div>
        <div class="form-group">
          <label class="form-label">Status</label>
          <select class="form-select"
            .value=${d.status || 'active'}
            @change=${(e) => this._emit('status', e.target.value)}>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
          </select>
        </div>
      </div>

      <!-- Park & Config -->
      <div class="form-section-title">Park & Configuration</div>
      <div class="form-row">
        <div class="form-group">
          <label class="form-label">UJID (Park ID)</label>
          <input type="number" class="form-input" placeholder="4821"
            .value=${d.ujid || ''}
            @input=${(e) => this._emit('ujid', e.target.value)}
          />
        </div>
        <div class="form-group">
          <label class="form-label">Park Name</label>
          <input type="text" class="form-input" placeholder="My RV Park"
            .value=${d.park_name || ''}
            @input=${(e) => this._emit('park_name', e.target.value)}
          />
        </div>
      </div>

      <div class="form-row">
        <div class="form-group">
          <label class="form-label">Timezone</label>
          <select class="form-select"
            .value=${d.timezone || 'America/New_York'}
            @change=${(e) => this._emit('timezone', e.target.value)}>
            <option value="America/New_York">Eastern (ET)</option>
            <option value="America/Chicago">Central (CT)</option>
            <option value="America/Denver">Mountain (MT)</option>
            <option value="America/Los_Angeles">Pacific (PT)</option>
            <option value="America/Anchorage">Alaska (AKT)</option>
            <option value="Pacific/Honolulu">Hawaii (HT)</option>
          </select>
        </div>
        <div class="form-group">
          <label class="form-label">Monthly Cost ($)</label>
          <input type="number" step="0.01" class="form-input" placeholder="4.50"
            .value=${d.monthly_cost || ''}
            @input=${(e) => this._emit('monthly_cost', e.target.value)}
            style="font-family: var(--font-mono);"
          />
        </div>
      </div>

      <!-- Notes -->
      <div class="form-group full">
        <label class="form-label">Notes</label>
        <textarea class="form-textarea" placeholder="Internal notes about this number..."
          .value=${d.notes || ''}
          @input=${(e) => this._emit('notes', e.target.value)}
        ></textarea>
      </div>
    `;
  }
}

customElements.define('phone-number-modal', PhoneNumberModal);
